import apiClient from '@/lib/axios';
import { User } from '@/services/types/auth';

// Types
export interface UserSearchParams {
  page?: number;
  limit?: number;
  type?: 'admin' | 'agent' | 'customer';
  search?: string;
  status?: 'active' | 'inactive';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startDate?: string;
  endDate?: string;
}

export interface UserSearchResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserStats {
  total: {
    users: number;
    active: number;
    inactive: number;
  };
  byType: {
    admin: { total: number; active: number; inactive: number };
    agent: { total: number; active: number; inactive: number };
    customer: { total: number; active: number; inactive: number };
  };
  recent: {
    total: number;
    admin: number;
    agent: number;
    customer: number;
  };
}

export interface UserActivity {
  id: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  metadata: any;
  createdAt: string;
}

export interface BulkOperationResult {
  successful: number;
  failed: number;
  results: Array<{ userId: string; success: boolean }>;
  errors: Array<{ userId: string; error: string }>;
}

export const userManagementApi = {
  // Get all users with filtering and pagination
  getUsers: async (params: UserSearchParams = {}): Promise<UserSearchResponse> => {
    const response = await apiClient.get('/api/admin/users', { params });
    return response.data;
  },

  // Get user by ID
  getUserById: async (id: string): Promise<{ success: boolean; data: User }> => {
    const response = await apiClient.get(`/api/admin/users/${id}`);
    return response.data;
  },

  // Create new user
  createUser: async (userData: {
    userType: 'admin' | 'agent' | 'customer';
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phone?: string;
    bio?: string;
    // Admin-specific fields
    permissions?: string[];
    // Agent-specific fields
    licenseNumber?: string;
    company?: string;
    experience?: number;
    specialties?: string[];
    languages?: string[];
    commissionRate?: number;
    // Customer-specific fields
    customerType?: 'buyer' | 'seller' | 'both';
    budget?: { min: number; max: number };
    preferredLocations?: string[];
    preferredPropertyTypes?: string[];
  }): Promise<{ success: boolean; data: User; message: string }> => {
    try {
      const response = await apiClient.post('/api/admin/users', userData);
      return response.data;
    } catch (error: any) {
      // Handle validation errors specifically
      if (error.response?.data?.message && error.response?.data?.message.includes('validation failed')) {
        const validationErrors = error.response.data.message;
        throw new Error(validationErrors);
      }
      throw error;
    }
  },

  // Update user
  updateUser: async (id: string, userData: Partial<User>): Promise<{ success: boolean; data: User; message: string }> => {
    try {
      const response = await apiClient.put(`/api/admin/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      // Handle validation errors specifically
      if (error.response?.data?.message && error.response?.data?.message.includes('validation failed')) {
        const validationErrors = error.response.data.message;
        throw new Error(validationErrors);
      }
      throw error;
    }
  },

  // Delete user (soft delete)
  deleteUser: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/api/admin/users/${id}`);
    return response.data;
  },

  // Toggle user status (activate/deactivate)
  toggleUserStatus: async (id: string): Promise<{ success: boolean; data: { id: string; isActive: boolean }; message: string }> => {
    const response = await apiClient.put(`/api/admin/users/${id}/toggle-status`);
    return response.data;
  },

  // Bulk operations
  bulkUserOperations: async (
    operation: 'activate' | 'deactivate' | 'delete' | 'update',
    userIds: string[],
    data?: any
  ): Promise<{ success: boolean; data: BulkOperationResult; message: string }> => {
    const response = await apiClient.post('/api/admin/users/bulk', {
      operation,
      userIds,
      data
    });
    return response.data;
  },

  // Get user statistics
  getUserStats: async (): Promise<{ success: boolean; data: UserStats }> => {
    const response = await apiClient.get('/api/admin/users/stats');
    return response.data;
  },

  // Get user activity
  getUserActivity: async (
    id: string,
    params: {
      page?: number;
      limit?: number;
      action?: string;
      resource?: string;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<{ success: boolean; data: UserActivity[]; pagination: any }> => {
    const response = await apiClient.get(`/api/admin/users/${id}/activity`, { params });
    return response.data;
  },

  // Search users (alternative endpoint if needed)
  searchUsers: async (query: string, params: UserSearchParams = {}): Promise<User[]> => {
    const response = await apiClient.get('/api/admin/users', {
      params: { search: query, ...params }
    });
    return response.data.data;
  },

  // Export users (if needed in future)
  exportUsers: async (params: UserSearchParams = {}): Promise<Blob> => {
    const response = await apiClient.get('/api/admin/users/export', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Import users (if needed in future)
  importUsers: async (file: File): Promise<{ success: boolean; data: any; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await apiClient.post('/api/admin/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};
